/**
 * ---------------------------------------------------------------------------------------------------------------------
 * BACK IN STOCK — KLAVIYO
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Defines the <back-in-stock-form> custom element used by the `back-in-stock-klaviyo` snippet. It validates the email,
 * submits a subscription to Klaviyo's public Back in Stock API and reports the result inline.
 *
 * The element is intentionally self-contained: it reads everything it needs (the Klaviyo key, API revision, variant ID
 * and the localized UI copy) from its own attributes, which are rendered fresh by Liquid. Because Impact re-renders the
 * product information on every variant change, the variant ID is always up to date without any client-side wiring.
 *
 * Notes on the integration with Impact:
 *   - We listen for the bubbling `submit` event on the element itself rather than querying the inner <form> on connect.
 *     A parent custom element's connectedCallback can run before its children are parsed, so delegation is the robust
 *     choice and also survives the element being moved around the DOM by <x-drawer>.
 *   - Impact's submit button (`is="custom-button"`) shows its own loading spinner via the `aria-busy` attribute, so we
 *     just toggle that instead of replacing the button label.
 *   - The connect/disconnect lifecycle uses an AbortController so the listener is cleanly re-bound when <x-drawer> moves
 *     this element to <body> while open, or when the product block is re-rendered.
 */

(() => {
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const DEFAULT_REVISION = '2024-10-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (customElements.get('back-in-stock-form')) {
    return;
  }

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this.addEventListener('submit', this._onSubmit.bind(this), {
        signal: this._abortController.signal,
      });
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    get companyId() {
      return this.getAttribute('company-id');
    }

    get revision() {
      return this.getAttribute('revision') || DEFAULT_REVISION;
    }

    get variantId() {
      return this.getAttribute('variant-id');
    }

    async _onSubmit(event) {
      event.preventDefault();

      if (this._submitting) {
        return;
      }

      const emailInput = this.querySelector('input[type="email"]');
      const email = (emailInput?.value || '').trim();

      if (!EMAIL_PATTERN.test(email)) {
        this._setBusy(false);
        this._showMessage(this.dataset.messageInvalidEmail, 'error');
        emailInput?.focus();
        return;
      }

      if (!this.variantId) {
        this._setBusy(false);
        this._showMessage(this.dataset.messageMissingVariant, 'error');
        return;
      }

      if (!this.companyId) {
        this._setBusy(false);
        console.error('[back-in-stock] Missing Klaviyo company id (company-id attribute).');
        this._showMessage(this.dataset.messageError, 'error');
        return;
      }

      this._submitting = true;
      this._setBusy(true);

      try {
        const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: this.revision,
          },
          body: JSON.stringify(this._buildPayload(email)),
        });

        if (!response.ok) {
          throw new Error(`Klaviyo responded with ${response.status}`);
        }

        this._showMessage(this.dataset.messageSuccess, 'success');
        event.target.reset();
      } catch (error) {
        // Keep the visible message generic/localized; log the technical detail for debugging.
        console.error('[back-in-stock] Subscription failed:', error);
        this._showMessage(this.dataset.messageError, 'error');
      } finally {
        this._submitting = false;
        this._setBusy(false);
      }
    }

    _buildPayload(email) {
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                // Klaviyo's external ID for a Shopify catalog variant.
                id: `$shopify:::$default:::${this.variantId}`,
              },
            },
          },
        },
      };
    }

    _setBusy(isBusy) {
      // Impact's `custom-button` renders a spinner while aria-busy is true.
      this.querySelector('[type="submit"]')?.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    _showMessage(text, state) {
      const messageElement = this.querySelector('[data-back-in-stock-message]');

      if (!messageElement) {
        return;
      }

      messageElement.textContent = text || '';
      messageElement.dataset.state = state;
      messageElement.hidden = !text;
    }
  }

  customElements.define('back-in-stock-form', BackInStockForm);
})();